import { useMemo } from 'react'
import type { MetricPoint } from '../lib/api'
import { humanBytes } from '../lib/api'

type MetricKind = 'cpu' | 'mem' | 'net' | 'disk' | 'load' | 'iowait'

interface Props {
  points: MetricPoint[]
  metric: MetricKind
  title: string
  height?: number
}

// пояснения того, что означает каждая метрика
const HINTS: Record<MetricKind, string> = {
  cpu:    'Загрузка процессора в процентах. 100% = один ядро занят полностью, при 8 ядрах максимум 800%.',
  mem:    'Занятая оперативная память. Указывается использовано / всего.',
  net:    'Суммарный трафик RX+TX в секунду.',
  disk:   'Занятое место на диске. Использовано / всего.',
  load:   'Load Average за 1 минуту — среднее число задач в очереди CPU. Норма ≤ количества ядер.',
  iowait: 'Процент времени когда CPU ждал диск/сеть. Больше 20% — узкое место в I/O.',
}

export function MetricsChart({ points, metric, title, height = 100 }: Props) {
  const { series, subtitle, current, avg, min, max } = useMemo(() => {
    const vals: number[] = []
    let total = 0

    for (const p of points) {
      let v = 0
      switch (metric) {
        case 'cpu':    v = p.cpu_percent; break
        case 'mem':    v = p.mem_used; break
        case 'net':    v = p.net_rx + p.net_tx; break
        case 'disk':   v = p.disk_used; break
        case 'load':   v = p.load1_x100 / 100; break
        case 'iowait': v = p.io_wait_pct; break
      }
      vals.push(v); total += v
    }

    const last = points[points.length - 1]
    let sub = ''
    if (last && (metric === 'mem' || metric === 'disk')) {
      const t = metric === 'mem' ? last.mem_total : last.disk_total
      sub = ` / ${humanBytes(t)}`
    }

    return {
      series: vals,
      subtitle: sub,
      current: vals[vals.length - 1] || 0,
      avg: vals.length ? total / vals.length : 0,
      min: vals.length ? Math.min(...vals) : 0,
      max: vals.length ? Math.max(...vals) : 0,
    }
  }, [points, metric])

  const fmt = (v: number): string => {
    if (metric === 'cpu' || metric === 'iowait') return v.toFixed(1) + '%'
    if (metric === 'mem' || metric === 'disk')   return humanBytes(v)
    if (metric === 'net')                        return humanBytes(v) + '/s'
    if (metric === 'load')                       return v.toFixed(2)
    return String(v)
  }

  const w = 100
  const h = 100
  const chartMax = Math.max(max, 1)
  const path = series.length > 1
    ? series.map((v, i) => {
        const x = (i / (series.length - 1)) * w
        const y = h - (v / chartMax) * h * 0.95 - 2
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
      }).join(' ')
    : ''

  const area = series.length > 1 ? path + ` L ${w} ${h} L 0 ${h} Z` : ''

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 group relative">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-[13px] font-medium text-neutral-800 flex items-center gap-1">
            {title}
            <span className="text-neutral-300 cursor-help text-[11px]" title={HINTS[metric]}>ⓘ</span>
          </div>
          {points.length > 0 && (
            <div className="text-[10px] text-neutral-400 mt-0.5">
              min {fmt(min)} · avg {fmt(avg)} · max {fmt(max)}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[15px] font-semibold tabular-nums leading-none">{fmt(current)}</div>
          {subtitle && <div className="text-[10px] text-neutral-400 mt-0.5 tabular-nums">{subtitle.trim()}</div>}
        </div>
      </div>
      <div style={{ height }} className="relative">
        {points.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-[12px] text-neutral-400">
            Нет данных
          </div>
        ) : (
          <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-full">
            {area && <path d={area} fill="rgba(23,23,23,0.06)" />}
            {path && <path d={path} fill="none" stroke="rgb(23,23,23)" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />}
          </svg>
        )}
      </div>
    </div>
  )
}
